import { api, createSafeQuery, fetchApi } from "@/lib/api";
import {
  type components,
  createOrganizationMembersHandlers,
  generateId,
  type MemberManagementActions as OrganizationMembersActions,
  type OrganizationRole,
  queryKeys,
} from "@space/shared";
import { useSession } from "@/lib/auth";

export function useOrganizationMembers(organizationId: () => string | undefined) {
  const session = useSession();

  const members = createSafeQuery<
    components["schemas"]["OrganizationMemberDTO"][],
    components["schemas"]["ErrorModel"]
  >(() => ({
    queryKey: (() => {
      const orgId = organizationId();
      return queryKeys.auth.members(orgId ?? "none");
    })(),
    queryFn: async () => {
      const orgId = organizationId();
      if (!orgId) {
        throw new Error("No organizationId");
      }
      return fetchApi(
        api.GET("/api/org/listMembers", {
          params: { query: { organizationId: orgId } },
        }),
      );
    },
    enabled: () => Boolean(session.data) && Boolean(organizationId()),
  }));

  const actions: OrganizationMembersActions<OrganizationRole> = {
    inviteMember: async (email: string, role: OrganizationRole) => {
      const orgId = organizationId();
      if (!orgId) {
        return;
      }
      await fetchApi(
        api.POST("/api/org/inviteMember", {
          body: {
            id: generateId(),
            organizationId: orgId,
            email,
            role,
          },
        }),
      );
      await members.refetch();
    },
    removeMember: async (memberIdOrUserId: string) => {
      const orgId = organizationId();
      if (!orgId) {
        return;
      }
      await fetchApi(
        api.POST("/api/org/removeMember", {
          body: {
            id: memberIdOrUserId,
          },
        }),
      );
      await members.refetch();
    },
    updateMemberRole: async (memberId: string, role: OrganizationRole) => {
      const orgId = organizationId();
      if (!orgId) {
        return;
      }
      await fetchApi(
        api.POST("/api/org/updateMemberRole", {
          body: {
            id: memberId,
            role,
          },
        }),
      );
      await members.refetch();
    },
  };

  const {
    handleInviteMember: inviteMember,
    handleRemoveMember: removeMember,
    handleUpdateMemberRole: updateMemberRole,
  } = createOrganizationMembersHandlers(actions);

  // ── AUTONOMY: member's campus + invitations issued by the org ─────────────

  const updateMemberCampus = async (memberId: string, campusId: string | null) => {
    await fetchApi(
      api.POST("/api/org/updateMemberCampus", {
        body: { id: memberId, campusId },
      }),
    );
    await members.refetch();
  };

  /** Invitation with pre-linked role + campus (F1-A `campusId`). */
  const inviteMemberWithCampus = async (
    email: string,
    role: OrganizationRole,
    campusId: string | null,
  ) => {
    const orgId = organizationId();
    if (!orgId) {
      return;
    }
    await fetchApi(
      api.POST("/api/org/inviteMember", {
        body: { id: generateId(), organizationId: orgId, email, role, campusId },
      }),
    );
    await orgInvitations.refetch();
  };

  const orgInvitations = createSafeQuery<
    components["schemas"]["InvitationDTO"][],
    components["schemas"]["ErrorModel"]
  >(() => ({
    queryKey: queryKeys.auth.orgInvitations(organizationId() ?? "none"),
    queryFn: async () => {
      const orgId = organizationId();
      if (!orgId) {
        throw new Error("No organizationId");
      }
      return fetchApi(
        api.GET("/api/org/listOrgInvitations", {
          params: { query: { organizationId: orgId } },
        }),
      );
    },
    enabled: () => Boolean(session.data) && Boolean(organizationId()),
  }));

  const revokeInvitation = async (invitationId: string) => {
    await fetchApi(api.POST("/api/org/cancelInvitation", { body: { invitationId } }));
    await orgInvitations.refetch();
  };

  return {
    members: () => members.data ?? [],
    isLoading: () => members.isLoading,
    inviteMember,
    removeMember,
    updateMemberRole,
    // AUTONOMY
    updateMemberCampus,
    inviteMemberWithCampus,
    orgInvitations: () => orgInvitations.data ?? [],
    orgInvitationsLoading: () => orgInvitations.isLoading,
    revokeInvitation,
  };
}
